/**
 * Verification Status Chip Component
 * Shows verification status with appropriate styling and icons
 */

import React from 'react';
import {
    CheckBadgeIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    DocumentTextIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import { VerificationStatus } from '../../types/verification';
import { STATUS_COLORS, VERIFICATION_MESSAGES } from '../../constants/verification';

interface VerificationStatusChipProps {
    status: VerificationStatus;
    size?: 'sm' | 'md' | 'lg';
    showIcon?: boolean;
    showText?: boolean;
    className?: string;
}

const VerificationStatusChip: React.FC<VerificationStatusChipProps> = ({
    status,
    size = 'md',
    showIcon = true,
    showText = true,
    className = ''
}) => {
    // Get status configuration
    const statusConfig = STATUS_COLORS[status];
    const statusMessage = VERIFICATION_MESSAGES.status[status];

    // Size configurations
    const sizeClasses = {
        sm: 'px-2 py-1 text-xs gap-1',
        md: 'px-3 py-1.5 text-sm gap-1.5',
        lg: 'px-4 py-2 text-base gap-2'
    };

    const iconSizes = {
        sm: 'w-3 h-3',
        md: 'w-4 h-4',
        lg: 'w-5 h-5'
    };

    // Get appropriate icon based on status
    const getStatusIcon = () => {
        const iconClass = iconSizes[size];

        switch (status) {
            case 'verified':
                return <CheckBadgeIcon className={`${iconClass} text-green-600`} />;
            case 'pending':
            case 'pending_review':
            case 'under_review':
                return <EyeIcon className={`${iconClass} text-blue-600`} />;
            case 'rejected':
                return <XCircleIcon className={`${iconClass} text-red-600`} />;
            case 'expired':
                return <ExclamationTriangleIcon className={`${iconClass} text-orange-600`} />;
            case 'incomplete':
                return <DocumentTextIcon className={`${iconClass} text-yellow-600`} />;
            case 'unverified':
            default:
                return <ClockIcon className={`${iconClass} text-neutral-600`} />;
        }
    };

    // Get display text based on status
    const getDisplayText = () => {
        switch (status) {
            case 'verified':
                return 'Verified';
            case 'pending':
                return 'Pending Review';
            case 'pending_review':
                return 'Pending Review';
            case 'under_review':
                return 'Under Review';
            case 'rejected':
                return 'Rejected';
            case 'expired':
                return 'Expired';
            case 'incomplete':
                return 'Incomplete';
            case 'unverified':
            default:
                return 'Not Verified';
        }
    };

    return (
        <span
            className={`
                inline-flex items-center font-medium rounded-full transition-colors
                ${sizeClasses[size]}
                ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border
                ${className}
            `}
            title={statusMessage}
        >
            {showIcon && getStatusIcon()}
            {showText && (
                <span className="font-medium">
                    {getDisplayText()}
                </span>
            )}
        </span>
    );
};

export default VerificationStatusChip;